import { useState } from 'react'
import { cn } from '@teach/ui'
import { formatCreditsWithWord } from '@/features/ai/lib/money'
import { usageDate, type UsageBar } from '@/features/ai/lib/usageBars'
import { uiLocale, useMessages } from '@/i18n'
import { useGetCreditPackagesQuery } from '@/features/ai/creditsApi'
import { approxPrice } from '@/features/ai/lib/approxPrice'
import { messages } from './messages'

const fmt = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(uiLocale(), options)
// Битая дата — прочерк, а не бросок: `format(Invalid Date)` бросает, и
// единственный ErrorBoundary приложения стоит в редакторе, а не здесь.
const day = (iso: string, long: boolean) => {
  const date = usageDate(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return fmt({ day: 'numeric', month: long ? 'long' : 'short' }).format(date)
}

/** Подпись наведённого дня: сколько потрачено или, если компенсаций больше, возвращено. */
const BarLabel = ({ bar }: { bar: UsageBar }) => {
  const m = useMessages(messages)
  const { data: packages } = useGetCreditPackagesQuery()
  const credits = bar.refund ? bar.spent.replace(/^-/, '') : bar.spent
  // Рядом с кредитами — примерная цена в деньгах («≈ $3.17»), если пакеты приехали.
  const money = approxPrice(credits, packages)
  const amount = `${formatCreditsWithWord(credits)}${money ? ` (≈ ${money})` : ''}`
  return (
    <span className="rounded-lg bg-popover px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
      {(bar.refund ? m.refundOn : m.spentOn)(amount, day(bar.date, true))}
    </span>
  )
}

/** Столбики трат по дням; наведение подсвечивает день и печатает его сумму. */
export const UsageChart = ({ bars }: { bars: UsageBar[] }) => {
  const [hovered, setHovered] = useState<number | null>(null)
  const active = hovered === null ? null : bars[hovered]
  return (
    <div className="relative px-5 pb-4 pt-4">
      <div className="flex h-20 items-end gap-[3px]" onMouseLeave={() => setHovered(null)}>
        {bars.map((bar, i) => (
          <div
            key={bar.date}
            onMouseEnter={() => setHovered(i)}
            className="flex h-full flex-1 items-end justify-center"
          >
            <span
              className={cn(
                'w-1.5 rounded-full transition-colors',
                i === hovered
                  ? 'bg-brand'
                  : bar.ratio > 0
                    ? 'bg-foreground/20'
                    : 'bg-foreground/10',
              )}
              // Пустой день — точка той же ширины, чтобы шкала дней читалась целиком.
              style={{ height: bar.ratio > 0 ? `${Math.max(bar.ratio * 100, 6)}%` : '6px' }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-muted-foreground">
        <span>{bars.length ? day(bars[0].date, false) : null}</span>
        <span>{bars.length ? day(bars[bars.length - 1].date, false) : null}</span>
      </div>
      {active ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <BarLabel bar={active} />
        </div>
      ) : null}
    </div>
  )
}
